if (!customElements.get('collection-stories')) {
  customElements.define('collection-stories', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.stories = Array.from(this.querySelectorAll('.ah-story-item'));
      this.dialog = this.querySelector('dialog');
      this.media = this.querySelector('#ah-modal-media-wrap');
      this.caption = this.querySelector('#ah-modal-caption');
      this.progress = this.querySelector('#ah-progress-bars');
      this.pauseButton = this.querySelector('.ah-story-pause');
      this.sound = this.querySelector('#ah-muted-hint');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.stories.forEach((story, index) => {
        story.addEventListener('click', () => this.open(index));
        story.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.open(index);
          }
        });
        const bar = document.createElement('div');
        bar.className = 'ah-progress-bar';
        bar.appendChild(Object.assign(document.createElement('div'), { className: 'ah-progress-fill' }));
        this.progress.appendChild(bar);
      });
      this.querySelector('#ah-modal-close').addEventListener('click', () => this.close());
      this.querySelector('#ah-modal-prev').addEventListener('click', () => this.navigate(-1));
      this.querySelector('#ah-modal-next').addEventListener('click', () => this.navigate(1));
      this.pauseButton.addEventListener('click', () => {
        this.paused = !this.paused;
        const video = this.media.querySelector('video');
        if (video) this.paused ? video.pause() : video.play().catch(() => { video.controls = true; });
        this.updatePause();
      });
      this.sound.addEventListener('click', () => {
        const video = this.media.querySelector('video');
        if (video) {
          video.muted = !video.muted;
          this.sound.textContent = video.muted ? 'Ton einschalten' : 'Ton ausschalten';
          this.sound.setAttribute('aria-label', this.sound.textContent);
        }
      });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.close(); });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.navigate(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.navigate(-1); }
      });
      this.dialog.addEventListener('touchstart', event => { this.touchX = event.touches[0].clientX; }, { passive: true });
      this.dialog.addEventListener('touchend', event => {
        const dx = event.changedTouches[0].clientX - this.touchX;
        if (Math.abs(dx) > 60) this.navigate(dx < 0 ? 1 : -1);
      }, { passive: true });
      this.addEventListener('shopify:block:select', event => {
        const index = this.stories.findIndex(story => story.contains(event.target));
        if (index >= 0) this.open(index);
      });
      if (this.reducedMotion) this.querySelectorAll('.ah-story-thumb-video').forEach(v => { v.pause(); v.removeAttribute('autoplay'); });
    }
    updatePause() {
      this.pauseButton.textContent = this.paused ? '▶' : 'Ⅱ';
      this.pauseButton.setAttribute('aria-label', this.paused ? 'Story fortsetzen' : 'Story pausieren');
    }
    open(index) {
      if (index < 0 || index >= this.stories.length) return;
      cancelAnimationFrame(this.frame);
      this.media.querySelector('video')?.pause();
      this.media.replaceChildren();
      this.current = index;
      this.elapsed = 0;
      this.previousTime = null;
      this.paused = this.reducedMotion;
      this.updatePause();
      const story = this.stories[index];
      story.querySelector('.ah-story-ring').classList.add('ah-seen');
      this.caption.textContent = story.dataset.caption;
      this.sound.classList.toggle('ah-show', story.dataset.type === 'video');
      if (!this.dialog.open) {
        this.oldOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.dialog.showModal();
      }
      this.dialog.classList.add('ah-open');
      const element = document.createElement(story.dataset.type === 'video' ? 'video' : 'img');
      element.className = 'ah-modal-media';
      element.src = story.dataset.src;
      if (element instanceof HTMLVideoElement) {
        element.playsInline = true;
        element.muted = true;
        element.preload = 'metadata';
        this.sound.textContent = 'Ton einschalten';
        this.sound.setAttribute('aria-label', 'Ton einschalten');
        element.addEventListener('ended', () => this.navigate(1));
        if (!this.paused) element.play().catch(() => { element.controls = true; });
      } else element.alt = story.dataset.caption;
      this.media.appendChild(element);
      this.querySelector('#ah-modal-prev').disabled = index === 0;
      this.querySelector('#ah-modal-next').disabled = index === this.stories.length - 1;
      this.paintProgress(0);
      this.frame = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      if (!this.dialog.open) return;
      const video = this.media.querySelector('video');
      const delta = this.previousTime == null ? 0 : time - this.previousTime;
      this.previousTime = time;
      if (!this.paused && !document.hidden) {
        if (video) {
          if (Number.isFinite(video.duration) && video.duration > 0) this.paintProgress(video.currentTime / video.duration);
        } else {
          const image = this.media.querySelector('img');
          if (image?.complete && image.naturalWidth > 0) this.elapsed += delta;
          this.paintProgress(Math.min(this.elapsed / 6000, 1));
          if (this.elapsed >= 6000) { this.navigate(1); return; }
        }
      }
      this.frame = requestAnimationFrame(next => this.tick(next));
    }
    paintProgress(ratio) {
      this.progress.querySelectorAll('.ah-progress-fill').forEach((fill, i) => {
        fill.style.width = (i < this.current ? 100 : i === this.current ? ratio * 100 : 0) + '%';
      });
    }
    navigate(direction) {
      const next = this.current + direction;
      if (next >= this.stories.length) this.close();
      else if (next >= 0) this.open(next);
    }
    close() {
      cancelAnimationFrame(this.frame);
      this.media.querySelector('video')?.pause();
      this.dialog.classList.remove('ah-open');
      this.dialog.close();
      this.media.replaceChildren();
      document.body.style.overflow = this.oldOverflow || '';
      this.stories[this.current]?.focus();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.media?.querySelector('video')?.pause();
      if (this.dialog?.open) { this.dialog.close(); document.body.style.overflow = this.oldOverflow || ''; }
    }
  });
}
